import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Phone, Store, Clock } from 'lucide-react';
import SEO from '@/components/SEO';
import { getCMSContent } from '@/lib/api';

const DAYS = ['lundi','mardi','mercredi','jeudi','vendredi','samedi','dimanche'];
const DAY_LABELS = { lundi:'Lundi', mardi:'Mardi', mercredi:'Mercredi', jeudi:'Jeudi', vendredi:'Vendredi', samedi:'Samedi', dimanche:'Dimanche' };

const NAPCard = ({ phone, cms }) => {
  const hasSchedule = DAYS.some(d => cms[`hours_${d}`]);
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-2xl p-6">
      <div className="flex flex-col sm:flex-row gap-6 items-start">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
          <Store className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Boutique</p>
              <p className="font-heading font-semibold text-foreground">Villa Marceau</p>
              <p className="text-sm text-muted-foreground">Concept Store</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Adresse</p>
              <p className="text-sm font-medium text-foreground">26 rue Marceau</p>
              <p className="text-sm text-muted-foreground">37000 Tours</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Téléphone</p>
              <p className="text-sm font-medium text-foreground flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {phone ? <a href={`tel:${phone}`} className="hover:text-primary transition-colors">{phone}</a> : <span className="italic text-muted-foreground">À renseigner</span>}
              </p>
            </div>
          </div>

          {hasSchedule && (
            <div className="border-t border-stone-200 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-primary shrink-0" />
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Horaires d'ouverture</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {DAYS.map(day => {
                  const hours = cms[`hours_${day}`];
                  if (!hours) return null;
                  const isClosed = hours.toLowerCase() === 'fermé' || hours.toLowerCase() === 'ferme';
                  return (
                    <div key={day} className="flex justify-between items-center py-1 px-2 rounded bg-white border border-stone-100 text-xs">
                      <span className="font-medium text-stone-600">{DAY_LABELS[day].slice(0,3)}.</span>
                      <span className={isClosed ? 'text-red-500' : 'text-stone-700'}>{hours}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function NotreHistoirePage() {
  const [cms, setCms] = useState({ phone_number: '', shop_hours: '' });

  useEffect(() => {
    getCMSContent().then(r => { if (r.data) setCms(r.data); }).catch(() => {});
  }, []);
  const storyParagraphs = [
    "Il était une fois une maison aux murs baignés de lumière, un lieu suspendu entre rêve et réalité, où chaque objet racontait une histoire… Cette maison, c'est Villa Marceau.",
    "Derrière cette porte, il y a moi, Garance. Une rêveuse, une voyageuse, une amoureuse des matières, des couleurs et des instants simples. Durant mes voyages, j'ai parcouru plusieurs pays avec les yeux grands ouverts, cherchant l'inspiration dans chaque détail. Mais c'est au Maroc que mon cœur s'est particulièrement posé : dans la chaleur des épices, la douceur des textiles, les nuances infinies des pigments, et ces odeurs envoûtantes qui restent gravées longtemps après le voyage.",
    "De ces souvenirs est née une envie : créer un lieu à mon image. Un cocon où l'on pourrait ressentir, toucher, respirer ailleurs sans quitter la ville.",
    "En 2026, à Tours, Villa Marceau ouvre ses portes.",
    "Plus qu'une boutique, c'est un concept store, un refuge. On y découvre une sélection de décoration et d'accessoires choisis avec soin, comme autant de fragments de voyages. Chaque pièce a une âme, une texture, une histoire à murmurer.",
    "Ici, le temps ralentit. Les couleurs réchauffent, les matières apaisent, et les parfums invitent à l'évasion.",
    "Mon souhait est simple : que chaque personne qui entre chez Villa Marceau se sente transportée dans un univers doux, coloré et enveloppant. Un lieu où l'on vient autant pour se faire plaisir que pour s'offrir un moment de calme, de rêve et de bien-être.",
    "Bienvenue dans mon monde. Bienvenue chez vous.",
  ];

  return (
    <PublicLayout>
      <SEO
        title="Notre Histoire — Le Concept Store Villa Marceau"
        description="Découvrez l'histoire de Villa Marceau, concept store bohème niché au cœur de Tours. Une boutique dédiée aux pièces uniques et à l'artisanat d'exception."
        canonical="/notre-histoire"
      />
      {/* Page Header */}
      <section className="pt-12 pb-8 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4 leading-tight">
              Notre Histoire
            </h1>
            <div className="w-16 h-0.5 bg-primary rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-10 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">

            {/* Text column — 3/5 */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <div className="space-y-5">
                {storyParagraphs.map((para, idx) => (
                  <p
                    key={idx}
                    className={`leading-relaxed text-foreground/80 ${
                      idx === 0
                        ? 'text-xl font-heading font-medium text-foreground italic border-l-4 border-primary pl-5 py-1'
                        : idx === 3
                        ? 'font-heading text-lg font-semibold text-primary'
                        : idx === storyParagraphs.length - 1
                        ? 'font-heading text-xl font-semibold text-foreground pt-2'
                        : 'text-base'
                    }`}
                  >
                    {para}
                  </p>
                ))}
              </div>

              {/* Signature */}
              <div className="mt-8 pt-6 border-t border-border/50">
                <p className="font-heading text-2xl italic text-primary">Garance</p>
                <p className="text-sm text-muted-foreground mt-1">Fondatrice de Villa Marceau</p>
              </div>
            </motion.div>

            {/* Photo column — 2/5 */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <div className="sticky top-28">
                <div className="relative">
                  {/* Photo */}
                  <div className="overflow-hidden rounded-2xl shadow-xl">
                    <img
                      src="/images/garance.jpg"
                      alt="Garance — fondatrice de Villa Marceau"
                      className="w-full h-auto object-cover"
                      loading="eager"
                    />
                  </div>
                  {/* Decorative background element */}
                  <div className="absolute -bottom-4 -right-4 w-full h-full bg-primary/8 rounded-2xl -z-10" />
                  <div className="absolute -top-4 -left-4 w-24 h-24 bg-stone-200/80 rounded-full -z-10" />
                </div>
                {/* Caption */}
                <div className="mt-4 text-center">
                  <p className="text-sm text-muted-foreground italic">
                    Garance dans sa boutique, Villa Marceau
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* NAP + Links */}
      <section className="py-12 px-6 lg:px-12 bg-stone-50/60">
        <div className="max-w-7xl mx-auto space-y-6">
          <NAPCard phone={cms.phone_number} cms={cms} />

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/commander-en-ligne"
              className="inline-flex items-center justify-center gap-2 bg-foreground text-white px-6 py-3 rounded-full font-medium hover:bg-foreground/90 transition-colors"
              data-testid="notre-histoire-shop-link"
            >
              Découvrir la boutique en ligne
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/ou-nous-trouver"
              className="inline-flex items-center justify-center gap-2 border border-border px-6 py-3 rounded-full font-medium text-foreground hover:bg-accent transition-colors"
              data-testid="notre-histoire-map-link"
            >
              <MapPin className="w-4 h-4 text-primary" />
              Nous rendre visite
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground px-6 py-3 transition-colors text-sm"
            >
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
